import { useLocalization } from "@fluent/react";
import { Button, useBreakpoints } from "@narmi/design_system";
import type Account from "byzantine/src/Account";
import type Transaction from "byzantine/src/Transaction";

import { EmptyTransactionState } from "../RecentTransactionsTable";
import SectionCard from "../SectionCard";
import TransactionLedger from "../../src/modules/liveLedger/components/TransactionLedger";
import { LIVE_LEDGER_ACCOUNT_QUERY_KEY } from "../../src/modules/liveLedger/constants";
import { LIVE_LEDGER_ROUTE } from "../../src/modules/liveLedger/routes";

import { useCurrentUser } from "../contexts/CurrentUserContext";
import { useUserFeatures } from "../contexts/UserFeaturesContext";

import TransactionsCard from "./TransactionsCard";
import { useAccountAnnouncementBanner } from "./useAccountAnnouncementBanner";

import type { TransactionLedgerColumnId } from "../../src/modules/liveLedger/components/TransactionLedger";

const CARD_COLUMNS: TransactionLedgerColumnId[] = [
  "date",
  "description",
  "amount",
];

const CARD_PAGE_SIZE = 10;

type AccountTransactionsLedgerCardProps = {
  account: Account;
  recentTransactions: Transaction[];
  replyTime: string;
};

const AccountTransactionsLedgerCard = ({
  account,
  recentTransactions,
  replyTime,
}: AccountTransactionsLedgerCardProps) => {
  const { l10n } = useLocalization();
  const { l } = useBreakpoints();
  const { currentUser } = useCurrentUser();
  const { live_ledger: liveLedgerEnabled } = useUserFeatures();
  const bannerEnabled = Boolean(liveLedgerEnabled) && currentUser !== null;
  const { dismiss: dismissAnnouncementBanner } = useAccountAnnouncementBanner(
    currentUser?.id?.toString() || "",
    bannerEnabled
  );

  if (!liveLedgerEnabled) {
    return (
      <TransactionsCard
        account={account}
        recentTransactions={recentTransactions}
        replyTime={replyTime}
      />
    );
  }

  const handleViewAll = () => {
    dismissAnnouncementBanner();
    // The account page and the ledger page are separate Django-mounted React
    // roots, so react-router's navigate() only pushes history state without
    // a real page transition here — a full navigation is required.
    window.location.assign(
      `/${LIVE_LEDGER_ROUTE}?${LIVE_LEDGER_ACCOUNT_QUERY_KEY}=${encodeURIComponent(
        account.id
      )}`
    );
  };

  return (
    <div data-testid="account-transactions-ledger-card">
      <SectionCard.Title
        className="margin--bottom--s"
        text={l10n.getString("recent-transactions-header")}
        button={
          <Button
            size="s"
            kind={l ? "tonal" : "plain"}
            endIcon="arrow-right"
            label={l10n.getString("recent-transactions-view-all")}
            onClick={handleViewAll}
            testId="account-transactions-view-all"
          />
        }
      />
      <TransactionLedger
        columns={CARD_COLUMNS}
        pagination={{ enabled: false, pageSize: CARD_PAGE_SIZE }}
        receipts
        scopedAccountIds={[account.id]}
        summary={false}
        stateStorage="local"
        emptyState={<EmptyTransactionState />}
        replyTime={replyTime}
      />
    </div>
  );
};

export default AccountTransactionsLedgerCard;
